import React, { ReactNode, createContext, useContext, useMemo, useState } from "react"

import { toast } from "sonner"

import { ReviewFormData, useUpdateTrialBlock } from "../hooks/useTrialBlockMutation"
import { useTrialBlocksFetch } from "../hooks/useTrialBlocksFetch"
import { SortConfig, TrialBlockContextType } from "../types/trialBlock"
import {
  filterTrialBlocksByDriverName,
  sortTrialBlocks,
  trialBlockDateRange,
} from "../utils/trialBlockUtils"

const TrialBlockContext = createContext<TrialBlockContextType | undefined>(undefined)

export const TrialBlockProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [selectedLocation, setSelectedLocation] = useState<string>("all")
  const [selectedBlock, setSelectedBlock] = useState<string | null>(null)
  const [showPendingOnly, setShowPendingOnly] = useState<boolean>(true)
  const [viewMode, setViewMode] = useState<"table" | "calendar">("calendar")
  const [sortConfig, setSortConfig] = useState<SortConfig>({
    key: null,
    direction: "ascending",
  })
  const [driverNameSearch, setDriverNameSearch] = useState<string>("")
  const [selectedDate, setSelectedDate] = useState<Date>(new Date())

  const {
    data: blocks = [],
    isLoading,
    error,
  } = useTrialBlocksFetch({
    dateRange: trialBlockDateRange(selectedDate, viewMode),
    location: selectedLocation !== "all" ? selectedLocation : undefined,
    showPendingOnly,
  })

  const requestSort = (key: string) => {
    let direction: "ascending" | "descending" = "ascending"
    if (sortConfig.key === key && sortConfig.direction === "ascending") {
      direction = "descending"
    }
    setSortConfig({ key, direction })
  }

  const filteredBlocks = useMemo(
    () => filterTrialBlocksByDriverName(sortTrialBlocks(blocks, sortConfig), driverNameSearch),
    [blocks, sortConfig, driverNameSearch],
  )

  const handleOpenReview = (id: string) => {
    setSelectedBlock(id)
  }

  const handleShowDetails = (id: string) => {
    setSelectedBlock(id)
  }

  // Use the mutation hook for updating trial blocks
  const updateTrialBlockMutation = useUpdateTrialBlock()

  const handleReviewSubmit = (id: string, formData: Record<string, unknown>) => {
    // Transform the form data to match our updated ReviewFormData interface
    const reviewData: ReviewFormData = {
      // Use trialShiftResult as the result field if available, otherwise fall back to reviewStatus or result
      result: formData.trialShiftResult as string,
      comments: formData.comments as string,
      // Explicitly map additionalTrial/requireAnotherTrial to ensure it's included
      additionalTrial: formData.requireAnotherTrial as boolean,
      // Add attended field from form data
      attended: formData.attended as boolean,
      // Set reviewed_at to current timestamp
      reviewed_at: new Date().toISOString(),
    }

    // Safely handle nested properties
    const deliveryProtocolScores = formData.deliveryProtocolScores as
      Record<string, unknown> | undefined
    if (deliveryProtocolScores) {
      reviewData.deliveryPictures = deliveryProtocolScores.deliveryPictures as string
      reviewData.deliveryNotes = deliveryProtocolScores.deliveryNotes as string
      reviewData.instructionCompliance = deliveryProtocolScores.instructionCompliance as string
    }

    // Handle deliverySuccessRate which is at the top level of the form
    const deliverySuccessRate = formData.deliverySuccessRate
    if (deliverySuccessRate !== undefined) {
      // Use the exact field name from the database: delivery_success
      reviewData.deliverySuccess = deliverySuccessRate as string
    }

    const roadSafetyScores = formData.roadSafetyScores as Record<string, unknown> | undefined
    if (roadSafetyScores) {
      reviewData.drivingBehaviour = roadSafetyScores.drivingBehaviour as string
      reviewData.vehicleHandling = roadSafetyScores.vehicleHandling as string
    }

    // Handle safetyScore which is at the top level of the form.
    // The schema validates/coerces it to a number (or undefined when blank);
    // only send it when present so we never cast a blank value to bigint.
    const safetyScore = formData.safetyScore
    if (typeof safetyScore === "number" && Number.isFinite(safetyScore)) {
      // Use the exact field name from the database: safety
      reviewData.safety = safetyScore
    }

    const customerInteractionScores = formData.customerInteractionScores as
      Record<string, unknown> | undefined
    if (customerInteractionScores) {
      reviewData.customerFeedback = customerInteractionScores.customerFeedback as string
      reviewData.issueResolution = customerInteractionScores.issueResolution as string
    }

    // Additional fields might come from the form or be undefined
    reviewData.timeManagement = formData.timeManagement as string
    reviewData.loadCheck = formData.loadCheck as string
    reviewData.vehicleName = formData.vehicleName as string
    reviewData.reviewStatus = "completed"

    // Add redirectUrl from environment variable for magic link
    const redirectUrl = import.meta.env.VITE_FRONTEND_URL || "https://deliver.hived.space"
    reviewData.redirectUrl = `${redirectUrl}/auth-callback`
    // Use the mutation with our transformed data
    updateTrialBlockMutation.mutate(
      {
        id,
        formData: reviewData,
      },
      {
        onSuccess: () => {
          // Close the review modal
          setSelectedBlock(null)

          toast.success("Review Submitted", {
            description: `Trial block review for ${
              blocks.find((b) => b.id === id)?.driverName
            } has been submitted successfully.`,
            duration: 5000,
          })
        },
        onError: (error) => {
          console.error("Error updating trial block:", error)
          toast.error("Failed to submit trial review", {
            description:
              error instanceof Error ? error.message : "Something went wrong. Please try again.",
          })
        },
      },
    )
  }

  const value = {
    blocks,
    selectedLocation,
    setSelectedLocation,
    showPendingOnly,
    setShowPendingOnly,
    viewMode,
    setViewMode,
    driverNameSearch,
    setDriverNameSearch,
    selectedBlock,
    setSelectedBlock,
    filteredBlocks,
    sortConfig,
    requestSort,
    handleOpenReview,
    handleShowDetails,
    handleReviewSubmit,
    selectedDate,
    setSelectedDate,
    isLoading,
    error,
  }

  return <TrialBlockContext.Provider value={value}>{children}</TrialBlockContext.Provider>
}

export const useTrialBlock = () => {
  const context = useContext(TrialBlockContext)
  if (context === undefined) {
    throw new Error("useTrialBlock must be used within a TrialBlockProvider")
  }
  return context
}
